import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Box,
  CircularProgress,
  LinearProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { CsvFieldSelector } from './CsvFieldSelector';
import { useExport } from '../../hooks/useExport';
import { logger } from '../../utils/logger';
import type { PdfProgressCallback } from '../../utils/pdfExport';

// Above this many items, PDF generation can be slow / memory-heavy in the
// browser, so we surface a soft warning (no hard cap — see issue #1208).
const LARGE_PDF_THRESHOLD = 150;

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  collectionId?: string;
  itemCount?: number;
  onExportPdf?: (
    includePrivateFields: boolean,
    onProgress?: PdfProgressCallback
  ) => Promise<void>;
  ownerId?: string;
  selectedIds?: string[];
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

export const ExportDialog = ({
  open,
  onClose,
  collectionId,
  itemCount,
  onExportPdf,
  ownerId,
  selectedIds,
}: ExportDialogProps) => {
  const hasSelection = !!selectedIds && selectedIds.length > 0;
  const effectiveCount = hasSelection ? selectedIds!.length : itemCount;
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const {
    fields,
    selectedFields,
    loading,
    exporting,
    error,
    includePrivateFields,
    includeOriginals,
    includeWeb,
    toggleField,
    selectAllInCategory,
    deselectAllInCategory,
    setIncludePrivateFields,
    setIncludeOriginals,
    setIncludeWeb,
    exportCsv,
    exportImages,
  } = useExport(ownerId);

  const handleExportCsv = async () => {
    try {
      await exportCsv(collectionId, selectedIds);
      onClose();
    } catch (error) {
      // Error already set in hook state, dialog stays open showing error
      logger.debug('CSV export failed', error);
    }
  };

  const handleExportImages = async () => {
    try {
      await exportImages(collectionId, selectedIds);
      onClose();
    } catch (error) {
      // Error already set in hook state, dialog stays open showing error
      logger.debug('Image export failed', error);
    }
  };

  const handleExportPdf = async () => {
    if (!onExportPdf) return;
    setPdfExporting(true);
    setPdfProgress(null);
    try {
      await onExportPdf(includePrivateFields, (completed, total) =>
        setPdfProgress({ completed, total })
      );
      onClose();
    } catch (error) {
      // Error logged; dialog stays open on failure
      logger.debug('PDF export failed', error);
    } finally {
      setPdfExporting(false);
      setPdfProgress(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Export Catalog
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {hasSelection ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {`Exporting ${selectedIds!.length} selected item${selectedIds!.length === 1 ? '' : 's'}`}
          </Typography>
        ) : (
          itemCount !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {collectionId
                ? `Exporting from selected album (${itemCount} items)`
                : `Exporting all items (${itemCount} items)`}
            </Typography>
          )
        )}

        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="CSV" />
          <Tab label="PDF" />
          <Tab label="Images" />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <CsvFieldSelector
              fields={fields}
              selectedFields={selectedFields}
              onToggleField={toggleField}
              onSelectAllInCategory={selectAllInCategory}
              onDeselectAllInCategory={deselectAllInCategory}
            />
          )}
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a PDF with one page per catalog item, mirroring the detail
            page layout.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includePrivateFields}
                onChange={(e) => setIncludePrivateFields(e.target.checked)}
              />
            }
            label="Include private fields (notes, value, location)"
          />
          {effectiveCount !== undefined && effectiveCount > LARGE_PDF_THRESHOLD && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {`Large export: ${effectiveCount} objects — this may take a while and use significant browser memory.`}
            </Alert>
          )}
          {pdfExporting && pdfProgress && pdfProgress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {`Preparing images ${pdfProgress.completed} of ${pdfProgress.total}…`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(pdfProgress.completed / pdfProgress.total) * 100}
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download a ZIP archive of catalog images.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeOriginals}
                onChange={(e) => setIncludeOriginals(e.target.checked)}
              />
            }
            label="Include original images"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeWeb}
                onChange={(e) => setIncludeWeb(e.target.checked)}
              />
            }
            label="Include web-optimized images (thumb, small, medium, large)"
          />
          {effectiveCount !== undefined && effectiveCount > 100 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Large export: {effectiveCount} items may take several minutes to
              download.
            </Alert>
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={exporting || pdfExporting}>
          Cancel
        </Button>
        {tabIndex === 0 && (
          <Button
            variant="contained"
            onClick={handleExportCsv}
            disabled={exporting || selectedFields.size === 0}
            startIcon={exporting ? <CircularProgress size={16} /> : undefined}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        )}
        {tabIndex === 1 && (
          <Button
            variant="contained"
            onClick={handleExportPdf}
            disabled={pdfExporting || !onExportPdf}
            startIcon={pdfExporting ? <CircularProgress size={16} /> : undefined}
          >
            {pdfExporting ? 'Generating PDF...' : 'Export PDF'}
          </Button>
        )}
        {tabIndex === 2 && (
          <Button
            variant="contained"
            onClick={handleExportImages}
            disabled={exporting || (!includeOriginals && !includeWeb)}
            startIcon={exporting ? <CircularProgress size={16} /> : undefined}
          >
            {exporting ? 'Exporting...' : 'Export Images'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
